/**
 * Regras puras das Redes sociais no front (testadas em social.test.ts). Datas e horários são
 * strings no horário de Brasília ("AAAA-MM-DDTHH:MM:SS") — nunca `new Date(valor)` (regra do repo).
 */
import type { MediaAsset } from '@/types/media';
import type {
  AspectRatio,
  MediaBrief,
  FormatRule,
  Network,
  PostFormat,
  PostStatus,
  SavePostRequest,
  SaveTargetInput,
  SocialAccount,
  SocialPost,
} from '@/types/social';

const HASHTAG_RE = /#[\p{L}\p{N}_]+/gu;

/** Hashtags do texto, sem repetir (ignora maiúsculas), na ordem. */
export const extractHashtags = (text?: string | null): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const h of (text ?? '').match(HASHTAG_RE) ?? []) {
    const k = h.toLowerCase();
    if (!seen.has(k)) {
      seen.add(k);
      out.push(h);
    }
  }
  return out;
};

/** Caracteres como as redes contam (pontos de código, não unidades UTF-16 — emoji conta 1). */
export const charCount = (text?: string | null): number => [...(text ?? '')].length;

/** Acrescenta as hashtags que ainda não estão no texto, numa linha nova. */
export const appendHashtags = (text: string, hashtags: string): string => {
  const existing = new Set(extractHashtags(text).map((h) => h.toLowerCase()));
  const add = extractHashtags(hashtags).filter((h) => !existing.has(h.toLowerCase()));
  if (add.length === 0) return text;
  const base = text.replace(/\s+$/, '');
  return `${base}${base ? '\n\n' : ''}${add.join(' ')}`;
};

/** Legenda efetiva de uma variação: a própria, se preenchida; senão a base. */
export const effectiveCaption = (base?: string | null, own?: string | null): string =>
  own && own.trim() !== '' ? own : base ?? '';

// ---------------------------------------------------------------- data e hora

/** "AAAA-MM-DDTHH:MM:SS" → "dd/mm/aaaa hh:mm". */
export const formatPostTime = (ts?: string | null): string => {
  if (!ts) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(ts);
  return m ? `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}` : ts;
};

/** Hora "HH:MM" do horário. */
export const timeOf = (ts?: string | null): string => (ts && ts.length >= 16 ? ts.slice(11, 16) : '');

/** Junta data (AAAA-MM-DD) e hora (HH:MM) no formato da API; sem data = null. */
export const joinDateTime = (date: string, time: string): string | null => {
  if (!date) return null;
  const t = /^\d{2}:\d{2}$/.test(time) ? time : '09:00';
  return `${date}T${t}`;
};

/** Agrupa os posts pelo dia do horário (chave AAAA-MM-DD), em ordem de hora. */
export const groupPostsByDay = (posts: SocialPost[]): Record<string, SocialPost[]> => {
  const out: Record<string, SocialPost[]> = {};
  for (const p of posts) {
    if (!p.scheduled_at) continue;
    (out[p.scheduled_at.slice(0, 10)] ??= []).push(p);
  }
  for (const list of Object.values(out)) list.sort((a, b) => (a.scheduled_at ?? '').localeCompare(b.scheduled_at ?? ''));
  return out;
};

/** Move para outro dia mantendo a hora (arrastar no calendário). */
export const moveToDay = (ts: string | undefined, day: string): string => `${day}T${timeOf(ts) || '09:00'}`;

// ---------------------------------------------------------------- grade

/** Reposiciona um item: devolve a nova ordem de ids (arrastar na grade). */
export const moveInOrder = (ids: number[], fromId: number, toId: number): number[] => {
  const from = ids.indexOf(fromId);
  const to = ids.indexOf(toId);
  if (from < 0 || to < 0 || from === to) return ids;
  const out = [...ids];
  out.splice(from, 1);
  out.splice(to, 0, fromId);
  return out;
};

/** Post já publicado (ou publicando) não sai do lugar na grade. */
export const isLocked = (status: PostStatus): boolean => status === 'published' || status === 'publishing';

/** Classe Tailwind de proporção para a miniatura do corte. */
export const ASPECT_CLASS: Record<AspectRatio, string> = {
  '1:1': 'aspect-square',
  '4:5': 'aspect-[4/5]',
  '9:16': 'aspect-[9/16]',
  '2:3': 'aspect-[2/3]',
  '16:9': 'aspect-video',
  original: 'aspect-[4/5]',
};

/** Proporção da grade de cada rede (Instagram mostra 3:4 no perfil desde 2025). */
export const GRID_ASPECT: Record<Network, string> = {
  instagram: 'aspect-[3/4]',
  facebook: 'aspect-square',
  pinterest: 'aspect-[2/3]',
  tiktok: 'aspect-[9/16]',
  youtube: 'aspect-[9/16]',
};

// ---------------------------------------------------------------- status

export const STATUS_CHIP: Record<PostStatus, string> = {
  strategy: 'bg-white text-slate-600 border border-dashed border-slate-300',
  draft: 'bg-slate-100 text-slate-700 border border-slate-200',
  awaiting_approval: 'bg-amber-50 text-amber-800 border border-amber-300',
  changes_requested: 'bg-rose-50 text-rose-700 border border-rose-300',
  approved: 'bg-emerald-50 text-emerald-800 border border-emerald-300',
  scheduled: 'bg-sky-50 text-sky-800 border border-sky-300',
  publishing: 'bg-indigo-50 text-indigo-700 border border-indigo-300',
  published: 'bg-emerald-600 text-white border border-emerald-700',
  partially_published: 'bg-orange-50 text-orange-700 border border-orange-300',
  failed: 'bg-red-100 text-red-700 border border-red-300',
};

/** Selo verde: passou pela aprovação do gestor. */
export const isApproved = (status: PostStatus): boolean =>
  ['approved', 'scheduled', 'publishing', 'published', 'partially_published', 'failed'].includes(status);

// ---------------------------------------------------------------- formulário

/** Corpo do salvar a partir do post carregado (mantém os IDs das variações). */
export const toSaveRequest = (p: SocialPost): SavePostRequest => {
  const mediaOf = (targetId?: number) =>
    p.media
      .filter((m) => (targetId === undefined ? m.target_id === undefined || m.target_id === null : m.target_id === targetId))
      .map((m) => ({
        media_id: m.media_id,
        aspect_ratio: m.aspect_ratio ?? null,
        focal_x: m.focal_x ?? null,
        focal_y: m.focal_y ?? null,
        alt_text: m.alt_text ?? null,
        cover_at_seconds: m.cover_at_seconds ?? null,
        cover_media_id: m.cover_media_id ?? null,
      }));
  return {
    title: p.title,
    caption: p.caption ?? '',
    first_comment: p.first_comment ?? '',
    notes: p.notes ?? '',
    pillar_id: p.pillar_id ?? null,
    scheduled_at: p.scheduled_at ? p.scheduled_at.slice(0, 16) : null,
    owner_member_id: p.owner_member_id ?? null,
    link_url: p.link_url ?? '',
    show_in_link_in_bio: p.show_in_link_in_bio,
    strategy: p.status === 'strategy',
    media: mediaOf(undefined),
    targets: p.targets.map((t) => ({
      id: t.id,
      account_id: t.account_id ?? null,
      format: t.format,
      caption: t.caption ?? '',
      title: t.title ?? '',
      link_url: t.link_url ?? '',
      board_name: t.board_name ?? '',
      privacy: t.privacy ?? '',
      options_json: t.options_json ?? '',
      media: mediaOf(t.id),
    })),
  };
};

/** Post vazio (novo). */
export const emptySaveRequest = (preset: Partial<SavePostRequest> = {}): SavePostRequest => ({
  title: '',
  caption: '',
  first_comment: '',
  notes: '',
  pillar_id: null,
  scheduled_at: null,
  owner_member_id: null,
  link_url: '',
  show_in_link_in_bio: false,
  strategy: false,
  media: [],
  targets: [],
  ...preset,
});

/** Formatos da rede, na ordem do catálogo. */
export const formatsOf = (rules: FormatRule[], network: Network): FormatRule[] => rules.filter((r) => r.network === network);

/** Rede do formato. */
export const networkOf = (rules: FormatRule[], format: PostFormat): Network | undefined =>
  rules.find((r) => r.format === format)?.network;

/** Converte o item da biblioteca no resumo usado pelos posts. */
export const toBrief = (m: MediaAsset): MediaBrief => ({
  id: m.id,
  kind: m.kind,
  title: m.title || m.original_file_name,
  status: m.status,
  width: m.width,
  height: m.height,
  duration_seconds: m.duration_seconds,
  size_bytes: m.size_bytes,
  ai_description: m.ai_description,
  thumb_url: m.thumb_url,
  preview_url: m.preview_url,
  deleted: false,
});

// ------------------------------------------------------------ perfis

/** Perfil que a tela usa: o pedido (?account=), senão o último usado, se ainda existir e for da rede. */
export const pickAccountId = (
  accounts: SocialAccount[],
  requested: number | null,
  stored: number | null,
  network?: Network,
): number | null => {
  const usable = accounts.filter((a) => a.is_active && (!network || a.network === network));
  for (const id of [requested, stored]) {
    if (id && usable.some((a) => a.id === id)) return id;
  }
  return null;
};

/** Nome curto do perfil: @usuário, ou o nome da Página/canal. */
export const accountLabel = (a: SocialAccount): string =>
  a.network === 'facebook' || a.network === 'youtube' ? a.display_name || a.username : `@${a.username}`;

/** Contas ativas agrupadas por rede, na ordem de NETWORKS da API (a lista já vem ordenada). */
export const groupAccountsByNetwork = (accounts: SocialAccount[]): { network: Network; accounts: SocialAccount[] }[] => {
  const out: { network: Network; accounts: SocialAccount[] }[] = [];
  for (const a of accounts) {
    if (!a.is_active) continue;
    let g = out.find((x) => x.network === a.network);
    if (!g) {
      g = { network: a.network, accounts: [] };
      out.push(g);
    }
    g.accounts.push(a);
  }
  return out;
};

/** Collab do Instagram: até 3 perfis (mesma regra de domain.ParseCollaborators no backend). */
export const MAX_COLLABORATORS = 3;

/**
 * Lê o campo "Collab com": @perfil, perfil ou o link do perfil, separados por vírgula, ponto e
 * vírgula ou espaço. Devolve os usernames válidos (minúsculos, sem repetir) e o que não serve.
 */
export const parseCollaborators = (raw?: string | null): { names: string[]; invalid: string[] } => {
  const names: string[] = [];
  const invalid: string[] = [];
  for (const part of (raw ?? '').trim().split(/[\s,;]+/)) {
    if (!part) continue;
    const v = part
      .toLowerCase()
      .replace(/^(?:https?:\/\/)?(?:www\.)?instagram\.com\//, '')
      .split('?')[0]
      .replace(/\/$/, '')
      .replace(/^@/, '');
    if (!/^[a-z0-9._]{1,30}$/.test(v)) invalid.push(part);
    else if (!names.includes(v)) names.push(v);
  }
  return { names, invalid };
};

/** Momento da capa do vídeo: 0:07,5 / 1:02. */
export const formatCoverTime = (s?: number | null): string => {
  const v = Math.max(0, s ?? 0);
  const min = Math.floor(v / 60);
  const sec = v - min * 60;
  const whole = Math.floor(sec);
  const tenth = Math.round((sec - whole) * 10);
  const base = `${min}:${String(tenth === 10 ? whole + 1 : whole).padStart(2, '0')}`;
  return tenth > 0 && tenth < 10 ? `${base},${tenth}` : base;
};

/**
 * A imagem de capa vale nesta lista de mídias? Na variação, se o formato aceita (Reels do
 * Instagram); nas mídias do post (aba Base), se alguma variação do post aceita.
 */
export const coverImageAllowed = (rules: FormatRule[], formats: PostFormat[], target: PostFormat | null): boolean => {
  const accepts = (f: PostFormat) => !!rules.find((r) => r.format === f)?.cover_image;
  return target ? accepts(target) : formats.some(accepts);
};

/** Número do perfil em pt-BR, curto como no app da rede: 987, 1,2 mil, 12 mil, 1,5 mi. */
export const formatCount = (n?: number | null): string => {
  if (n === undefined || n === null) return '—';
  const short = (v: number, unit: string) => `${(Math.floor(v * 10) / 10).toLocaleString('pt-BR', { maximumFractionDigits: v < 100 ? 1 : 0 })} ${unit}`;
  if (n >= 1_000_000) return short(n / 1_000_000, 'mi');
  if (n >= 10_000) return `${Math.floor(n / 1000).toLocaleString('pt-BR')} mil`;
  if (n >= 1000) return short(n / 1000, 'mil');
  return n.toLocaleString('pt-BR');
};

/** Variações iniciais de um post novo: os formatos pedidos ou, com um perfil escolhido, o 1º formato da rede dele. */
/** Posição da variação deste formato nesta conta (-1 = não existe); cada par formato + conta é uma variação. */
export const findTargetIndex = (targets: Pick<SaveTargetInput, 'format' | 'account_id'>[], format: PostFormat, accountId: number): number =>
  targets.findIndex((t) => t.format === format && t.account_id === accountId);

export const presetTargets = (rules: FormatRule[], formats?: PostFormat[], account?: Pick<SocialAccount, 'id' | 'network'>): SaveTargetInput[] => {
  const list = formats?.length ? formats : account ? formatsOf(rules, account.network).slice(0, 1).map((r) => r.format) : [];
  return list.map((format) => ({
    format,
    account_id: account && networkOf(rules, format) === account.network ? account.id : undefined,
    media: [],
  }));
};
